import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Users, Trash2, Edit2, Shield, User } from "lucide-react";

const ROLES = [
  { value:"admin", label:"Admin", color:"bg-red-100 text-red-700" },
  { value:"administracao", label:"Administração", color:"bg-blue-100 text-blue-700" },
  { value:"advogado", label:"Advogado", color:"bg-green-100 text-green-700" },
];
const EMPTY = { email:"", password:"", nome:"", apelido:"", role:"advogado", telemovel:"", username:"" };

export default function UtilizadoresList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const { user: currentUser } = useAuth();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await axios.get(`${API}/utilizadores`, { headers: getAuthHeader() });
    setUsers(data); setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const canManageUsers = currentUser?.role === "admin" || currentUser?.role === "administracao";

  const openNew = () => { setEditId(null); setForm(EMPTY); setError(""); setShowModal(true); };
  const openEdit = (u) => {
    setEditId(u.id);
    setForm({ email:u.email||"", password:"", nome:u.nome||"", apelido:u.apelido||"", role:u.role||"advogado", telemovel:u.telemovel||"", username:u.username||"" });
    setError(""); setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError("");
    try {
      if (editId) {
        const upd = { nome:form.nome, apelido:form.apelido, role:form.role, telemovel:form.telemovel };
        if (form.password) upd.password = form.password;
        await axios.put(`${API}/utilizadores/${editId}`, upd, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API}/utilizadores`, form, { headers: getAuthHeader() });
      }
      setShowModal(false); load();
    } catch(err) {
      setError(err.response?.data?.detail || "Erro ao guardar");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar utilizador?")) return;
    await axios.delete(`${API}/utilizadores/${id}`, { headers: getAuthHeader() });
    load();
  };

  const handleToggleActive = async (u) => {
    await axios.put(`${API}/utilizadores/${u.id}`, { is_active: !u.is_active }, { headers: getAuthHeader() });
    load();
  };

  const getRoleBadge = (role) => ROLES.find(r=>r.value===role) || { label: role, color:"bg-slate-100 text-slate-700" };

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Users size={22} className="text-[#0b679e]"/> Utilizadores</h1>
          <p className="text-slate-500 text-sm mt-0.5">{users.length} utilizador(es)</p>
        </div>
        {canManageUsers && (
          <button onClick={openNew} data-testid="novo-utilizador-btn"
            className="flex items-center gap-2 bg-[#0b679e] hover:bg-[#084e78] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
            <Plus size={16}/> Novo Utilizador
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="w-7 h-7 border-4 border-[#0b679e] border-t-[#F5A623] rounded-full animate-spin"/></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>{["Nome","Email","Username","Perfil","Telemóvel","Estado","Ações"].map(h=>(
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map(u => {
                  const badge = getRoleBadge(u.role);
                  return (
                    <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-[#0b679e] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {(u.nome?.[0]||"U").toUpperCase()}
                          </div>
                          <span className="font-medium text-slate-900">{u.nome} {u.apelido}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{u.email}</td>
                      <td className="px-4 py-3 text-slate-500 font-mono text-xs">{u.username||"—"}</td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge.color}`}>{badge.label}</span></td>
                      <td className="px-4 py-3 text-slate-500">{u.telemovel||"—"}</td>
                      <td className="px-4 py-3">
                        {canManageUsers ? (
                          <button onClick={()=>handleToggleActive(u)}
                            className={`text-xs px-2 py-0.5 rounded-full font-medium cursor-pointer ${u.is_active!==false?"bg-green-100 text-green-700":"bg-red-100 text-red-700"}`}>
                            {u.is_active!==false ? "Ativo" : "Inativo"}
                          </button>
                        ) : (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.is_active!==false?"bg-green-100 text-green-700":"bg-red-100 text-red-700"}`}>
                            {u.is_active!==false ? "Ativo" : "Inativo"}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {canManageUsers && (
                          <div className="flex items-center gap-2">
                            <button onClick={()=>openEdit(u)} className="text-slate-400 hover:text-slate-600 p-1 transition-colors"><Edit2 size={14}/></button>
                            {u.id !== currentUser?.id && (
                              <button onClick={()=>handleDelete(u.id)} className="text-slate-400 hover:text-red-500 p-1 transition-colors"><Trash2 size={14}/></button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Credentials Note */}
      {canManageUsers && (
        <div className="bg-[#0b679e]/5 border border-[#0b679e]/20 rounded-xl p-4">
          <p className="text-sm text-[#0b679e] font-medium mb-1">Credenciais Padrão</p>
          <p className="text-xs text-slate-600">Password padrão para novos utilizadores: <code className="bg-slate-100 px-1.5 py-0.5 rounded font-mono">Portal@2024</code></p>
          <p className="text-xs text-slate-500 mt-1">Recomenda-se que cada utilizador altere a sua password após o primeiro acesso.</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">{editId ? "Editar Utilizador" : "Novo Utilizador"}</h3>
              <button onClick={()=>setShowModal(false)}><X size={18}/></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Nome *</label>
                  <input value={form.nome} onChange={e=>setForm({...form,nome:e.target.value})} required data-testid="user-nome"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Apelido *</label>
                  <input value={form.apelido} onChange={e=>setForm({...form,apelido:e.target.value})} required
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/></div>
              </div>
              {!editId && (
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Email *</label>
                  <input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} required
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/></div>
              )}
              <div><label className="block text-xs font-medium text-slate-600 mb-1">{editId ? "Nova Password (opcional)" : "Password *"}</label>
                <input type="password" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} required={!editId} placeholder={editId?"Deixar em branco para não alterar":""}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Perfil</label>
                  <select value={form.role} onChange={e=>setForm({...form,role:e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]">
                    {ROLES.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}
                  </select></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Telemóvel</label>
                  <input value={form.telemovel} onChange={e=>setForm({...form,telemovel:e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/></div>
              </div>
              {error && <p className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">{error}</p>}
              <div className="flex justify-end gap-3 pt-2 border-t border-slate-200">
                <button type="button" onClick={()=>setShowModal(false)} className="px-4 py-2 border border-slate-300 rounded-lg text-sm">Cancelar</button>
                <button type="submit" disabled={saving} data-testid="save-utilizador-btn"
                  className="px-4 py-2 bg-[#0b679e] hover:bg-[#084e78] text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center gap-2">
                  {saving&&<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>}
                  {editId ? "Atualizar" : "Criar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
